import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { ALL_TAB_PERMISSIONS, ALL_RESOURCE_PERMISSIONS, TAB_LABELS, RESOURCE_LABELS } from "@/lib/permissions";
import { getStoredToken, parseJwt } from "@/types";
import type { OrgCustomRole } from "@/types";

export function OrgRolesTab({ orgId }: { orgId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.orgRoles(orgId),
    queryFn: () => api.listOrgRoles(orgId),
  });

  const [editingRole, setEditingRole] = useState<OrgCustomRole | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const token = getStoredToken();
  const currentUserRole = token ? parseJwt(token)?.role : undefined;
  const isSuperAdmin = currentUserRole === "super_admin";

  const allSystemRoles = data?.system_roles ?? [];
  const systemRoles = isSuperAdmin ? allSystemRoles : allSystemRoles.filter((r) => r.name !== "super_admin");
  const customRoles = data?.custom_roles ?? [];

  return (
    <div className="space-y-6 px-5 py-4">
      {/* Custom roles header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-near-black">Custom Roles</h4>
          <p className="text-[11px] text-text-secondary">Org-specific roles with their own permission sets.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="shrink-0 rounded-btn bg-near-black px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-rausch"
        >
          + New Custom Role
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-secondary">Loading roles…</p>
      ) : (
        <>
          {customRoles.length === 0 ? (
            <div className="rounded-btn border-2 border-dashed border-border-card bg-surface-secondary p-6 text-center text-xs text-text-secondary">
              No custom roles yet. Create one to define org-specific access levels.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {customRoles.map((role) => (
                <RoleCard
                  key={role.id}
                  name={role.label}
                  codeName={role.name}
                  permissions={role.permissions}
                  badge="custom"
                  onEdit={() => setEditingRole(role)}
                  onDelete={<DeleteRoleButton orgId={orgId} roleId={role.id} />}
                />
              ))}
            </div>
          )}

          {/* System roles */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
              System Roles (read-only)
            </p>
            <div className="flex flex-col gap-3">
              {systemRoles.map((role) => (
                <RoleCard
                  key={role.name}
                  name={role.label}
                  codeName={role.name}
                  permissions={role.permissions}
                  badge="system"
                />
              ))}
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <CreateRoleDialog orgId={orgId} onClose={() => setShowCreate(false)} />
      )}
      {editingRole && (
        <EditRoleDialog orgId={orgId} role={editingRole} onClose={() => setEditingRole(null)} />
      )}
    </div>
  );
}

function RoleCard({
  name,
  codeName,
  permissions,
  badge,
  onEdit,
  onDelete,
}: {
  name: string;
  codeName: string;
  permissions: string[];
  badge: "system" | "custom";
  onEdit?: () => void;
  onDelete?: React.ReactNode;
}) {
  const tabPerms = permissions.filter((p) => p.startsWith("tab:"));
  const resourcePerms = permissions.filter((p) => !p.startsWith("tab:"));

  return (
    <div className="rounded-card bg-white p-4 shadow-card">
      <div className="mb-3 flex items-start justify-between border-b border-surface-secondary pb-3">
        <div className="flex items-center gap-2">
          <div>
            <h5 className="text-sm font-semibold text-near-black">{name}</h5>
            <code className="text-[10px] text-text-secondary">{codeName}</code>
          </div>
          <span
            className={`rounded-badge px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              badge === "system"
                ? "bg-surface-secondary text-text-focused"
                : "bg-indigo-50 text-indigo-700"
            }`}
          >
            {badge}
          </span>
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center gap-1.5">
            {onEdit && (
              <button
                onClick={onEdit}
                className="rounded-btn border border-border-card px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-near-black"
              >
                Edit
              </button>
            )}
            {onDelete}
          </div>
        )}
      </div>

      {tabPerms.length > 0 && (
        <div className="mb-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
            Navigation Access
          </p>
          <div className="flex flex-wrap gap-1">
            {tabPerms.map((p) => (
              <span key={p} className="rounded-badge bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                {TAB_LABELS[p] ?? p.replace("tab:", "")}
              </span>
            ))}
          </div>
        </div>
      )}

      {resourcePerms.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
            Permissions
          </p>
          <div className="flex flex-wrap gap-1">
            {resourcePerms.map((p) => {
              const cls = p.endsWith(":write")
                ? "bg-amber-50 text-amber-700"
                : p.endsWith(":read")
                ? "bg-emerald-50 text-emerald-700"
                : "bg-surface-secondary text-text-focused";
              return (
                <span key={p} className={`rounded-badge px-2 py-0.5 text-[11px] font-medium ${cls}`}>
                  {RESOURCE_LABELS[p] ?? p}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {tabPerms.length === 0 && resourcePerms.length === 0 && (
        <p className="text-xs text-text-secondary">No permissions assigned.</p>
      )}
    </div>
  );
}

function DeleteRoleButton({ orgId, roleId }: { orgId: number; roleId: number }) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.deleteOrgRole(orgId, roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgRoles(orgId) }),
  });

  if (confirm) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rounded-btn bg-rose-600 px-2 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {mutation.isPending ? "…" : "Confirm"}
        </button>
        <button onClick={() => setConfirm(false)} className="text-xs text-text-secondary hover:text-near-black">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirm(true)}
      className="rounded-btn border border-rose-200 px-2.5 py-1 text-xs font-medium text-error-text hover:bg-rose-50"
    >
      Delete
    </button>
  );
}

function PermissionGroup({
  title,
  items,
  selected,
  onToggle,
}: {
  title: string;
  items: { value: string; label: string }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">{title}</p>
      <div className="space-y-1">
        {items.map((item) => (
          <label key={item.value} className="flex cursor-pointer items-center gap-2.5 rounded-btn px-2 py-1.5 hover:bg-surface-secondary">
            <input
              type="checkbox"
              checked={selected.has(item.value)}
              onChange={() => onToggle(item.value)}
              className="h-4 w-4 rounded border-border-card accent-near-black"
            />
            <span className="text-sm text-near-black">{item.label}</span>
            <code className="ml-auto text-[10px] text-text-secondary">{item.value}</code>
          </label>
        ))}
      </div>
    </div>
  );
}

function CreateRoleDialog({ orgId, onClose }: { orgId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.createOrgRole(orgId, { name, label, permissions: Array.from(selected) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgRoles(orgId) });
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Failed to create role"),
  });

  function toggle(value: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  const autoName = (val: string) => {
    setLabel(val);
    setName(val.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-card bg-white shadow-card">
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
          <h3 className="text-lg font-semibold text-near-black">New Custom Role</h3>
          <button onClick={onClose} className="rounded-btn px-2 py-1 text-text-secondary hover:bg-surface-secondary">✕</button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => autoName(e.target.value)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                placeholder="Custom Reviewer"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Name (slug)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                placeholder="custom_reviewer"
              />
            </div>
          </div>

          <PermissionGroup title="Navigation Access" items={ALL_TAB_PERMISSIONS} selected={selected} onToggle={toggle} />
          <PermissionGroup title="Permissions" items={ALL_RESOURCE_PERMISSIONS} selected={selected} onToggle={toggle} />

          {error && <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-surface-secondary px-5 py-3">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black">Cancel</button>
          <button
            onClick={() => { setError(""); mutation.mutate(); }}
            disabled={!label || !name || mutation.isPending}
            className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {mutation.isPending ? "Creating…" : "Create Role"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRoleDialog({ orgId, role, onClose }: { orgId: number; role: OrgCustomRole; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(role.label);
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.updateOrgRole(orgId, role.id, { label, permissions: Array.from(selected) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgRoles(orgId) });
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Failed to update role"),
  });

  function toggle(value: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-card bg-white shadow-card">
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
          <div>
            <h3 className="text-lg font-semibold text-near-black">Edit Role: {role.label}</h3>
            <code className="text-[11px] text-text-secondary">{role.name}</code>
          </div>
          <button onClick={onClose} className="rounded-btn px-2 py-1 text-text-secondary hover:bg-surface-secondary">✕</button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
          <PermissionGroup title="Navigation Access" items={ALL_TAB_PERMISSIONS} selected={selected} onToggle={toggle} />
          <PermissionGroup title="Permissions" items={ALL_RESOURCE_PERMISSIONS} selected={selected} onToggle={toggle} />

          {error && <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-surface-secondary px-5 py-3">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black">Cancel</button>
          <button
            onClick={() => { setError(""); mutation.mutate(); }}
            disabled={!label || mutation.isPending}
            className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
